import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Gift, Search, MapPin, Truck, Star } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Badge, Input, Drawer, AutoComplete } from 'antd';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pincode, setPincode] = useState('');
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [pincodesData, setPincodesData] = useState({ pincodes: [] });
  const [productsData, setProductsData] = useState({ products: [] });
  const [combosData, setCombosData] = useState({ combos: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { cart } = useCart();
  const navigate = useNavigate();

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  // Fetch data from actual JSON files
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // First try from public/data folder
        const [pincodesRes, productsRes, combosRes] = await Promise.all([
          fetch('/data/pincodes.json').catch(() => null),
          fetch('/data/products.json').catch(() => null),
          fetch('/data/combos.json').catch(() => null)
        ]);

        // Process pincodes response
        if (pincodesRes && pincodesRes.ok) {
          const pincodesText = await pincodesRes.text();
          // Check if response is valid JSON (not HTML)
          if (pincodesText.trim().startsWith('{') || pincodesText.trim().startsWith('[')) {
            const pincodes = JSON.parse(pincodesText);
            setPincodesData(pincodes || { pincodes: [] });
          } else {
            console.error('Invalid JSON response for pincodes:', pincodesText.substring(0, 100));
          }
        }

        // Process products response
        if (productsRes && productsRes.ok) {
          const productsText = await productsRes.text();
          if (productsText.trim().startsWith('{') || productsText.trim().startsWith('[')) {
            const products = JSON.parse(productsText);
            setProductsData(products || { products: [] });
          } else {
            console.error('Invalid JSON response for products:', productsText.substring(0, 100));
          }
        }

        // Process combos response
        if (combosRes && combosRes.ok) {
          const combosText = await combosRes.text();
          if (combosText.trim().startsWith('{') || combosText.trim().startsWith('[')) {
            const combos = JSON.parse(combosText);
            setCombosData(combos || { combos: [] });
          } else {
            console.error('Invalid JSON response for combos:', combosText.substring(0, 100));
          }
        }

        setDataLoaded(true);
      } catch (error) {
        console.error('Error in fetchData:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get featured products for mobile drawer
  useEffect(() => {
    if (dataLoaded) {
      const allProducts = [...productsData.products, ...combosData.combos];
      const featured = allProducts.slice(0, 4);
      setFeaturedProducts(featured);
    }
  }, [dataLoaded, productsData, combosData]);

  // Check pincode delivery
  const checkDelivery = (pincodeValue) => {
    const pincodeData = pincodesData.pincodes?.find(p => p.pincode === pincodeValue);
    if (pincodeData) {
      setDeliveryInfo({
        available: pincodeData.deliveryAvailable,
        city: pincodeData.city,
        days: pincodeData.deliveryDays === 'same' ? 'Same Day' : `${pincodeData.deliveryDays} Day${pincodeData.deliveryDays > 1 ? 's' : ''}`
      });
    } else {
      setDeliveryInfo({
        available: false,
        city: null,
        days: null
      });
    }
  };

  // Handle pincode change
  const handlePincodeChange = (value) => {
    const numericValue = value.replace(/\D/g, '');
    setPincode(numericValue);
    if (numericValue.length === 6) {
      checkDelivery(numericValue);
    } else if (numericValue.length === 0) {
      setDeliveryInfo(null);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    if (searchTerm.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
      setIsMenuOpen(false);
    }
  };

  // Search suggestions
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value.trim() && dataLoaded) {
      const allProducts = [...productsData.products, ...combosData.combos];
      const results = allProducts
        .filter(product => 
          product.name.toLowerCase().includes(value.toLowerCase()) ||
          product.tags?.some(tag => tag.toLowerCase().includes(value.toLowerCase())) ||
          product.category?.toLowerCase().includes(value.toLowerCase())
        )
        .slice(0, 4)
        .map(product => ({
          value: product.name,
          label: (
            <div className="flex items-center py-1">
              <img 
                src={product.image} 
                alt={product.name}
                className="w-8 h-8 object-cover rounded mr-2"
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium">{product.name}</span>
                <span className="text-xs text-gray-500">₹{product.price}</span>
              </div>
            </div>
          ),
          product
        }));
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectSearch = (value, option) => {
  
    if (option.product) {
      navigate(`/product/${option.product.type}/${option.product.id}`);
      setSearchTerm('');
      setSearchResults([]);
      setIsMenuOpen(false);
    }
  };

  // Get delivery status color
  const getDeliveryStatusColor = () => {
    if (!deliveryInfo) return 'text-gray-300';
    return deliveryInfo.available ? 'text-green-300' : 'text-red-300';
  };

  // Get delivery icon
  const getDeliveryIcon = () => {
    if (!deliveryInfo) return <Truck className="h-3 w-3" />;
    return deliveryInfo.available ? (
      <div className="flex items-center">
        <Truck className="h-3 w-3 mr-1" />
        <span>✓</span>
      </div>
    ) : (
      <div className="flex items-center">
        <Truck className="h-3 w-3 mr-1" />
        <span>✗</span>
      </div>
    );
  };

  // Get delivery message
  const getDeliveryMessage = () => {
    if (!deliveryInfo) return 'Check delivery';
    if (!deliveryInfo.available) return 'Not available';
    return `${deliveryInfo.city} - ${deliveryInfo.days}`;
  };

  // Navigation links
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/shop', label: 'Shop All' },
    { path: '/shop?category=roses', label: 'Roses' },
    { path: '/shop?category=combo', label: 'Combos' },
    { path: '/shop?category=teddy-bears', label: 'Teddy' },
    { path: '/about', label: 'About' },
  ];

  return (
    <>
      {/* Top Delivery Bar - Better Mobile View */}
    <div
  style={{
    background: "linear-gradient(to right, #be185d, #9d174d)",
    color: "#fff",
    padding: "8px 12px",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "10px",
      whiteSpace: "nowrap",
    }}
  >
    {/* LEFT */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
      }}
    >
      <Truck size={14} />
      <span style={{ fontSize: "13px", fontWeight: 500 }}>
        Delivery Check:
      </span>
    </div>

    {/* RIGHT */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexShrink: 0,
      }}
    >
      {/* PINCODE INPUT */}
      <div style={{ position: "relative" }}>
        <MapPin
          size={12}
          style={{
            position: "absolute",
            left: "8px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#d1d5db",
          }}
        />
        <input
          type="text"
          placeholder="Enter Pincode"
          value={pincode}
          maxLength={6}
          onChange={(e) => handlePincodeChange(e.target.value)}
          style={{
            padding: "4px 8px 4px 26px",
            width: "130px",
            fontSize: "12px",
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "4px",
            color: "#fff",
            outline: "none",
          }}
        />
      </div>

      {/* STATUS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "12px",
          fontWeight: 500,
        }}
      >
        {getDeliveryIcon()}
        <span>{getDeliveryMessage()}</span>
      </div>
    </div>
  </div>
</div>

      {/* Main Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-3 md:px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Mobile Menu Button */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <button
                className="md:hidden p-1.5"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="h-5 w-5 text-gray-700" />
                ) : (
                  <Menu className="h-5 w-5 text-gray-700" />
                )}
              </button>

              <Link to="/" className="flex items-center space-x-2" onClick={() => setIsMenuOpen(false)}>
                <Gift className="h-7 w-7 md:h-8 md:w-8 text-pink-600" />
                <div>
                  <span className="text-lg md:text-2xl font-bold text-pink-700 leading-tight">Sohkin Gifts</span>
                  <p className="hidden xs:block text-xs text-gray-500 -mt-0.5">Premium Flowers & Gifts</p>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-gray-700 hover:text-pink-600 font-medium text-sm lg:text-base transition-colors duration-200 relative group px-1"
                >
                  {link.label}
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-pink-600 group-hover:w-full transition-all duration-300"></span>
                </Link>
              ))}
            </nav>

            {/* Search and Cart - Responsive */}
            <div className="flex items-center space-x-3 md:space-x-4 lg:space-x-6">
              {/* Search - Hidden on very small screens */}
              <div className="hidden xs:block relative">
                <AutoComplete
                  options={searchResults}
                  onSelect={handleSelectSearch}
                  onSearch={handleSearchChange}
                  value={searchTerm}
                  className="w-32 sm:w-40 md:w-48 lg:w-56"
                  placeholder="Search..."
                  size="small"
                  dropdownClassName="search-dropdown"
                >
                  <Input.Search
                    onSearch={handleSearch}
                    enterButton={<Search className="h-3.5 w-3.5" />}
                    size="small"
                  />
                </AutoComplete>
              </div>

              {/* Mobile Search Button */}
              <button
                className="xs:hidden p-1.5 text-gray-700 hover:text-pink-600"
                onClick={() => setIsDrawerOpen(true)}
                aria-label="Search products"
              >
                <Search className="h-5 w-5" />
              </button>

              {/* Cart */}
              <Link 
                to="/cart" 
                className="relative p-1.5 md:p-2 rounded-full hover:bg-pink-50 transition-colors duration-200 group"
                onClick={() => setIsMenuOpen(false)}
              >
                <Badge 
                  count={cartCount} 
                  showZero 
                  className="bg-pink-600 text-white text-xs min-w-[18px] h-[18px] border-2 border-white"
                  offset={[-4, 4]}
                  style={{ fontSize: '10px', lineHeight: '18px' }}
                >
                  <ShoppingCart className="h-5 w-5 md:h-6 md:w-6 text-gray-700 group-hover:text-pink-600 transition-colors duration-200" />
                </Badge>
              </Link>

              {/* Mobile Products Drawer Button */}
              <button
                className="md:hidden p-1.5 text-gray-700 hover:text-pink-600"
                onClick={() => setIsDrawerOpen(true)}
                aria-label="View products"
              >
                <Star className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Mobile Menu - Slide down animation */}
          {isMenuOpen && (
            <div className="md:hidden mt-3 pb-3 border-t pt-3 animate-slideDown">
              <div className="flex flex-col space-y-2">
                {/* Pincode Check in Mobile Menu */}
                <div className="px-3 pb-2 border-b">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">Check Delivery</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Input
                      placeholder="Enter pincode"
                      value={pincode}
                      onChange={(e) => handlePincodeChange(e.target.value)}
                      className="flex-1"
                      size="small"
                      maxLength={6}
                    />
                    {deliveryInfo && (
                      <div className={`px-2 py-1 rounded text-xs font-medium ${deliveryInfo.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {deliveryInfo.available ? '✓' : '✗'}
                      </div>
                    )}
                  </div>
                  
                  {/* Quick pincodes in mobile menu */}
                  {pincodesData.pincodes?.length > 0 && (
                    <div className="flex items-center space-x-2 mt-2 overflow-x-auto pb-1">
                      <span className="text-xs text-gray-500 whitespace-nowrap">Try:</span>
                      {pincodesData.pincodes.slice(0, 4).map(p => (
                        <button
                          key={p.pincode}
                          onClick={() => handlePincodeChange(p.pincode)}
                          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0"
                        >
                          {p.city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-gray-700 hover:text-pink-600 hover:bg-pink-50 py-2 px-3 rounded-lg transition-colors duration-200 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="text-sm font-medium">{link.label}</span>
                  </Link>
                ))}

                {/* Mobile Search */}
                <div className="px-3 pt-2">
                  <AutoComplete
                    options={searchResults}
                    onSelect={handleSelectSearch}
                    onSearch={handleSearchChange}
                    value={searchTerm}
                    className="w-full"
                    placeholder="Search products..."
                    size="small"
                    dropdownClassName="search-dropdown"
                  >
                    <Input.Search
                      onSearch={handleSearch}
                      enterButton={<Search className="h-3.5 w-3.5" />}
                      size="small"
                    />
                  </AutoComplete>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Products Drawer - Ant Design */}
      <Drawer
        title={
          <div className="flex items-center space-x-2">
            <Gift className="h-5 w-5 text-pink-600" />
            <span className="text-base font-semibold">Featured Products</span>
          </div>
        }
        placement="right"
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        width={300}
        closeIcon={<X className="h-4 w-4" />}
        headerStyle={{ padding: '16px' }}
        bodyStyle={{ padding: '16px' }}
      >
        <div className="space-y-4">
          <div className="bg-pink-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600">
              Browse our premium collection of gifts and flowers
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <p className="text-gray-500">Loading products...</p>
            </div>
          ) : featuredProducts.length > 0 ? (
            <>
              <div className="space-y-2">
                {featuredProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.type}/${product.id}`}
                    className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors duration-200 group"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-800 group-hover:text-pink-600 truncate">
                        {product.name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {product.type === 'combo' ? 'Combo Package' : 
                         product.category?.replace('-', ' ')}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="font-bold text-pink-600 text-sm">
                          ₹{product.price}
                        </span>
                        {product.originalPrice && (
                          <span className="text-xs text-gray-400 line-through">
                            ₹{product.originalPrice}
                          </span>
                        )}
                        {product.discount && (
                          <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                            {product.discount}% off
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="pt-2 border-t">
                <Link
                  to="/shop"
                  className="block w-full text-center bg-gradient-to-r from-pink-600 to-pink-700 text-white py-2 rounded-lg hover:from-pink-700 hover:to-pink-800 transition-all duration-200 text-sm font-medium"
                  onClick={() => setIsDrawerOpen(false)}
                >
                  View All Products
                </Link>
                
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Link
                    to="/shop?category=roses"
                    className="text-center bg-gray-100 text-gray-800 py-2 rounded-lg hover:bg-gray-200 text-xs font-medium"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    Roses
                  </Link>
                  <Link
                    to="/shop?category=combo"
                    className="text-center bg-gray-100 text-gray-800 py-2 rounded-lg hover:bg-gray-200 text-xs font-medium"
                    onClick={() => setIsDrawerOpen(false)}
                  >
                    Combos
                  </Link>
                </div>
              </div>

              {/* Delivery Info in Drawer */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-700">Delivery Info</p>
                    <p className="text-xs text-gray-500">Check pincode above</p>
                  </div>
                  <Truck className="h-4 w-4 text-blue-600" />
                </div>
                
                {deliveryInfo && (
                  <div className={`mt-2 p-2 rounded ${deliveryInfo.available ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className="text-xs font-medium">
                      {deliveryInfo.available ? '✓ Delivery Available' : '✗ Not Available'}
                    </p>
                    {deliveryInfo.city && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        {deliveryInfo.city} - {deliveryInfo.days}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <Gift className="h-12 w-12 text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">No featured products available</p>
              <Link
                to="/shop"
                className="mt-4 text-pink-600 hover:text-pink-700 text-sm font-medium"
                onClick={() => setIsDrawerOpen(false)}
              >
                Browse all products →
              </Link>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default Header;